<template>
  <app-page-container :showNav="true" :background="'calendar'" :UID="user ? user._id : ''">
    <!-- <custom-calendar-background> -->
    <div class="booking-container" :class="$isMobile ? 'booking-container--mobile' : ''">
      <Alert :showAlert="showAlert" :alertStyle="alertStyle" :alertMessage="alertMessage" />

      <!-- Goal is to award badges one at a time, thus only passing one -->
      <BadgeAwardModal v-if="unawardedBadges && unawardedBadges.length" />
      <div class="full flex-column-between">
        <div class="full calendar-container">
          <TopBar
            v-if="shrinkedView"
            :shrinkedView="shrinkedView"
            :nextMatchDateTimeMS="nextMatchDateTimeMS"
            :selectedDateTimes="selectedDateTimes"
            :bookingOrViewing="bookingOrViewing"
          />
          <div class="cofocus-core" :style="getCofocusCoreStyles">
            <div class="calendar" :style="settingsOpen ? 'border-radius:20px' : ''">
              <Settings v-if="settingsOpen" :styles="themeStyles" :userID="user._id" />
              <div class="calendar-view" v-show="!settingsOpen">
                <Switcher
                  :viewStartMS="viewStartMS"
                  :viewEndMS="viewEndMS"
                  :userID="user._id"
                  :week="week"
                  :currentSelectedDay="currentSelectedDay"
                  @setAsDay="setAsDay"
                />
                <Table
                  :viewStartMS="viewStartMS"
                  :viewEndMS="viewEndMS"
                  :selectedDateTimes="selectedDateTimes"
                  :currentlyBooking="currentlyBooking"
                  :minutesToNextSession="minutesToNextSession"
                  :nextMatchDateTimeMS="nextMatchDateTimeMS"
                  :week="week"
                  :currentUserData="currentUserData"
                  :lastMinMS="lastMinMS"
                  :calendarData="calendarData"
                  :pastMatches="pastMatches"
                  :upcomingMatches="upcomingMatches"
                  :matchPool="matchPool"
                  :calendarUsers="calendarUsers"
                  :prefer24HourFormat="prefer24HourFormat"
                />
              </div>
            </div>
            <div
              class="sidebar"
              :class="getSidebarClasses"
              v-show="!settingsOpen && (!shrinkedView || selectedDateTimes.length)"
              :style="styles.sidebar"
            >
              <!-- <Tasks v-if="tasksOpen" /> -->
              <div class="widgets" :style="styles.widget">
                <CalendarTimer
                  v-if="!shrinkedView"
                  :bookingOrViewing="bookingOrViewing"
                  :nextMatchDateTimeMS="nextMatchDateTimeMS"
                  :selectedDateTimes="selectedDateTimes"
                  :shrinkedView="shrinkedView"
                  :topBarView="false"
                />
                <Booker
                  v-if="selectedDateTimes.length"
                  :selectedDateTimes="selectedDateTimes"
                  :selectionSessionData="selectionSessionData"
                  :currentlyBooking="currentlyBooking"
                  :matchPool="matchPool"
                  :calendarUsers="calendarUsers"
                  :prefer24HourFormat="prefer24HourFormat"
                  :shrinkedView="shrinkedView"
                />
                <NoBookings v-if="nextMatchDateTimeMS < 0 && !selectedDateTimes.length && !shrinkedView" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <!--  <div v-show="shrinkedView">
        <iframe data-id="cofocus.one_970x90_anchor_DFP" frameborder="0" scrolling="no" marginheight="0" marginwidth="0" topmargin="0" leftmargin="0" width="1" height="1"></iframe>
      </div> -->
    </div>
    <!-- </custom-calendar-background> -->
  </app-page-container>
</template>

<script>
/**
 Most efficient way of rendering calendar and its contents?
1. First pre-render calendar layout based on configuration
2. Then begin displaying sessions based on config
3. CSS based on config, e.g. height of a box
4. Time starts by default either from 12AM Monday OR your specified time slot
This app needs to be the absolute fastest.
We hate slow apps. With our guts.
Life's too short for a bad app experience.
No more shitty product experiences.
Enough is enough.
Welcome to Cofocus.
 */
import api from "../../api/cofocusAPI";
/* import calendarAPIService from "../../store/calendar/controllers/calendarAPIService"; */
import { mapState } from "vuex";
import CalendarTimer from "../../components/Timer/CalendarTimer";
import Alert from "../../components/Alert";
// import CustomCalendarBackground from "@/customizableComponents/CustomCalendarBackground";
import AppPageContainer from "@/components/AppPageContainer";
import Switcher from "./Table/Switcher";
import Table from "./Table/Table";
import Booker from "./Widgets/Booker/Booker";
import NoBookings from "./Widgets/NoBookings";
import TopBar from "./TopBar";
const Settings = () => ({
  component: import("./Settings/Settings"),
});
import BadgeAwardModal from "../../components/BadgeSystem/BadgeAwardModal";
import { startOfDay, startOfISOWeek, format } from "date-fns";
import calendarSocket from "../../socket/calendarSocket";
import { ViewController } from "../../store/calendar";
import { SESSION_LENGTH_MS } from "../../constants_calendar";
import { checkVisibilitySupport } from "../../utilities/utilities";

import { BadgeStore } from "../../ELYX/ELYXStore";
import BadgeMethods from "../../ELYX/Badges/BadgeMethods";

import CalendarMethods from "../../ELYX/Calendar/CalendarMethods";

import ELYXUtils from "../../ELYX/ELYXUtils";
import CalendarUtilities from "../../ELYX/Calendar/CalendarUtilities";

import TasksStore from "../../components/Nav/Dropdowns/TasksStore.js";

/* import Tasks from "../../components/Nav/Dropdowns/Tasks.vue"; */

const ONE_WEEK = 604800000;
const ONE_DAY = 86400000;
const FIFTEEN_MINUTES = 15 * 60 * 1000;
// const MAX_SESSION_LENGTH_MS = 50*60*1000;
export default {
  name: "BookingDashboard",
  data() {
    return {
      adsLoaded: false,
      alertMessage: "",
      alertStyle: "",
      showAlert: false,
      // databaseSyncTimer: "",
      pastHourTimer: "",
      gettingBookedSessions: false,
      gettingBookedSessionsError: false,
      gettingAllBookedSessions: false,
      gettingAllBookedSessionsError: false,
      interval: 15,
      height: 100,
      iterativeRefreshInterval: 2 * 60 * 1000,
      lastCheckDateTime: startOfDay(Date.now()),
      currentlyRefreshingData: false,
      lastMinMS: Date.now(),

      visibilityChange: "",
      visibilityState: "",

      lastRefocusInMS: 0,

      sessionNotifierIntervalId: null,
    };
  },
  components: {
    // CustomCalendarBackground,
    AppPageContainer,
    CalendarTimer,
    Switcher,
    Table,
    Booker,
    Alert,
    NoBookings,
    BadgeAwardModal,
    Settings,
    TopBar,
    /* Tasks, */
  },
  computed: {
    unawardedBadges: () => BadgeStore.state.unawardedBadges,

    ...mapState({
      user: (state) => state.auth.user,
      isAuthenticated: (state) => state.auth.authenticationStatus,
      isVerified: (state) => state.auth.verificationStatus,
      /* calendar meta */
      viewStartMS: (state) => state.calendar.viewStartMS,
      viewEndMS: (state) => state.calendar.viewEndMS,
      week: (state) => state.calendar.week,
      currentUserData: (state) => state.calendar.currentUserData,
      calendarData: (state) => state.calendar.calendarData,
      /* calendar people */
      pastMatches: (state) => state.pastMatches.pastMatches,
      calendarUsers: (state) => state.calendarUsers.users,
      matchPool: (state) => state.matchPool.matchPoolUsers,
      upcomingMatches: (state) => state.upcomingMatches.upcomingMatches,
      /* selections */
      selectedDateTimes: (state) => state.selections.selectedDateTimes,
      selectionSessionData: (state) => state.selections.selectionSessionData,
      currentlyBooking: (state) => state.selections.currentlyBooking,
      /* preferences */
      prefer24HourFormat: (state) => state.cofocusPreferences.calendarPreferences.prefer24HourFormat,
      /* section open states */
      settingsOpen: (state) => state.cofocusPreferences.settingsOpen,
      /* next session management */
      minutesToNextSession: (state) => state.upcomingMatches.minutesToNextSession,
      nextMatchDateTimeMS: (state) => state.upcomingMatches.nextMatchDateTimeMS,
      themeStyles: (state) => state.style.styles,
      onboardingComplete: (state) => state.onboarding.onboardingComplete,
      /* Session guidelines modal */
    }),
    tasksOpen: () => TasksStore.state.isOpen,
    userNotificationPreferences() {
      return this.user.notificationPreferences;
    },
    nextSession() {
      console.log("@nextSession: ", this.$store.getters["upcomingMatches/nextMatchData"]);
      console.log("upcomingMatches: ", this.upcomingMatches);
      const nextSession = this.$store.getters["upcomingMatches/nextMatchData"];
      return nextSession ? nextSession : null;
    },
    nextSessionDateTimeMS() {
      if (this.nextSession) return this.nextSession.dateTimeMS;
      return null;
    },
    getSidebarClasses() {
      let classes = "";
      if (this.shrinkedView) {
        classes += "sidebar-mini" + " ";
      }
      if (this.tasksOpen) {
        classes += "sidebar-tasks" + " ";
      }
      return classes;
    },
    currentSelectedDay() {
      /* If view covers the current moment, returns the start of this day */
      if (this.viewStartMS < Date.now() && this.viewEndMS > Date.now()) {
        return startOfDay(Date.now());
      }
      return this.viewStartMS;
    },
    styles() {
      const styles = {
        widget: "",
        sidebar: "",
      };
      if (this.nextMatchDateTimeMS < 0 && !this.selectedDateTimes.length) {
        styles.widget = "justify-content: center;";
        styles.sidebar = "background-color: rgb(255 255 255 / 73%);";
      }
      // else if (this.selectedDateTimes.length) {
      //   styles.widget = "margin-top: unset;";
      // }
      return styles;
    },
    bookingOrViewing() {
      return !!this.selectedDateTimes && !!this.selectedDateTimes.length;
    },
    shrinkedView() {
      let shrinked = false;
      if (this.$windowWidth < 1280 && this.week) {
        shrinked = true;
      } else if (this.$windowWidth < 1220) {
        shrinked = true;
      }
      return shrinked;
    },
    getCofocusCoreStyles() {
      let styles = "";
      if (this.$windowWidth < 1280 && this.week) {
        styles = "height: calc(100vh - 124px) !important;";
      }
      if (this.$windowWidth < 909) {
        styles = "";
      }
      return styles;
    },
  },
  beforeRouteLeave(to, from, next) {
    console.log("beforeRouteLeave", to, from, this.$store);
    this.cleanBeforeLeave(true, next);
  },
  beforeDestroy() {
    this.cleanBeforeLeave();
  },
  async created() {
    window.addEventListener("beforeunload", this.cleanBeforeLeave);
    const self = this;
    this.adsLoaded = true;

    /* Initializing calendar view */
    ViewController.initializeCalendar(this.user._id).then(async () => {
      self.$nextTick(() => {
        /* has to be nexttick since must happen after Table.vue has loaded */
        self.scrollToCurrentHour();
        window.mixpanel.track("Visit Calendar Page");
      });
      /* suboptimal but whatever */
      if (self.$windowWidth <= 800) {
        self.setAsDay();
      }
      /* Initialize calendar socket */
      await calendarSocket.initCalendarSocket();

      /** Get user's booking restriction data */
      await CalendarMethods.getUserBookingData();

      /* Initialize tab refocusing auto-updated */
      await self.initRefocusUpdate();

      /** Get user's unawarded badges if any */
      await BadgeMethods.getUserUnawardedBadges();
    });
  },
  async mounted() {
    document.addEventListener("click", this.closeMiniNav);
    // Consider also listening to 'rematches'.
    // console.log("@Step 4: Setup cleaning for when you leave.");

    // ON TIMERS: https://stackoverflow.com/a/2553507
    // Sync calendar data with database
    // (make iterative check, to only update if change)
    // this.databaseSyncTimer = setInterval(
    //   this.syncCalendarWithDatabase,
    //   this.iterativeRefreshInterval
    // );
    this.pastHourTimer = setInterval(this.updateLastMinMS, 1 * 60 * 1000);

    // const url =
    //   "//strangelyfaintestgreenhouse.com/d05ecff270b089951afb436e30dff25b/invoke.js";
    // /* Check if script exists */
    // if (document.querySelectorAll(`script[src="${url}"]`).length > 0) {
    //   let scriptElement = document.querySelectorAll(`script[src="${url}"]`);
    //   console.log(scriptElement);
    // } else {
    //   let ad = document.createElement("script");
    //   ad.setAttribute(
    //     "src",
    //     "//strangelyfaintestgreenhouse.com/d05ecff270b089951afb436e30dff25b/invoke.js"
    //   );
    //   ad.setAttribute("async", "async");
    //   ad.setAttribute("data-cfasync", "false");
    //   document.head.appendChild(ad);
    // }
    window.addEventListener("online", this.refocus);

    this.initSessionNotifier();
  },
  methods: {
    async cleanBeforeLeave(fromBeforeLeave = false, next = null) {
      /* closeSocket(); */
      // TODO add clean all calendar data
      // this.$refs.timer.resetSessionAndTimer();
      document.removeEventListener("click", this.closeMiniNav);
      window.removeEventListener("online", this.refocus);
      window.removeEventListener("beforeunload", this.cleanBeforeLeave);
      document.removeEventListener(this.visibilityChange, this.refocus);
      // this.resetData();
      // clearInterval(this.databaseSyncTimer);
      clearInterval(this.pastHourTimer);
      clearInterval(this.sessionNotifierIntervalId);
      if (fromBeforeLeave && next !== null) {
        next();
      }
    },
    /* runAds() {
      let shrinked = false;
      if (this.$windowWidth < 1280 && this.week) {
        shrinked = true;
      } else if (this.$windowWidth < 1220) {
        shrinked = true;
      }

      const url = shrinked ? "/bottom-a.js" : "/calendar-a.js";
      // Check if script exists
      if (document.querySelectorAll(`script[src="${url}"]`).length > 0) {
        let scriptElement = document.querySelectorAll(`script[src="${url}"]`);
        console.log(scriptElement);
      } else {
        let ad = document.createElement("script");
        ad.setAttribute("src", url);
        ad.setAttribute("async", "async");
        ad.setAttribute("data-cfasync", "false");
        document.head.appendChild(ad);
      }
    }, */
    /* reinitAds() {
      var size = "300x250",
        adunit = "cofocus.one_300x250_medium_rectangle_DFP",
        childNetworkId = "22663861536",
        xmlhttp = new XMLHttpRequest();
      xmlhttp.onreadystatechange = function() {
        if (xmlhttp.readyState == 4 && xmlhttp.status == 200) {
          var es = document.querySelectorAll("[data-id='" + adunit + "']");
          var e = Array.from(es).filter(function(e) {
            return !e.hasAttribute("data-rendered");
          });
          if (e.length > 0) {
            e.forEach(function(el) {
              var iframe = el.contentWindow.document;
              iframe.open();
              iframe.write(xmlhttp.responseText);
              iframe.close();
              el.setAttribute("data-rendered", true);
            });
          }
        }
      };
      var child = childNetworkId.trim() ? "," + childNetworkId.trim() : "";
      xmlhttp.open("GET", "https://pubads.g.doubleclick.net/gampad/adx?iu=/147246189" + child + "/" + adunit + "&sz=" + encodeURI(size) + "&t=Placement_type%3Dserving&" + Date.now(), true);
      xmlhttp.send();
    }, */
    async initRefocusUpdate() {
      /* When person refocuses */
      const { visibilityChange, visibilityState } = checkVisibilitySupport();
      if (visibilityChange != null && visibilityState != null) {
        this.visibilityChange = visibilityChange;
        this.visibilityState = visibilityState;
        /* Check if content is visible */
        document.addEventListener(visibilityChange, this.refocus, false);
      }
    },
    async refocus() {
      /* Check if content is visible OR if document has focus post visibility change */
      if (document[this.visibilityState] == "visible" || document.hasFocus()) {
        console.log("Tab is visible OR got refocused OR went online again!");
        /* If > 8 seconds passed since last update, update again
        Doesn't matter too much as it's a rare case someone madly switches tabs */
        const nowInMS = Date.now();
        const fifteenSecondsInMS = 8 * 1000;
        const timeSinceRefocusMS = nowInMS - this.lastRefocusInMS;
        if (timeSinceRefocusMS > fifteenSecondsInMS) {
          await this.refreshCalendarData(false);
          this.lastRefocusInMS = Date.now();
          /* console.log("refreshed"); */
        } else {
          /* console.log("too early"); */
          console.log("time in seconds since last update", timeSinceRefocusMS / 1000);
        }
      } else if (document[this.visibilityState] == "hidden") {
        console.log("Tab lost visibility");
      } else if (!document.hasFocus()) {
        console.log("Tab lost focus");
      }
    },
    updateLastMinMS() {
      /* console.log("UPDATING!", this.lastMinMS); */
      this.lastMinMS = Date.now();
      this.changeViewIfNecessary();
      this.removeSelectionsInPast();
      this.completePastMatch();
    },

    async computeUserSessionConclusions() {
      try {
        await api.REQUEST({
          method: "post",
          url: "api/sessions/upcoming/conclude",
          data: {
            UID: this.user._id,
          },
          withCredentials: true,
        });
        /* console.log("respon", response); */
      } catch (error) {
        console.error("@computeUserSessionConclusions, error: ", error);
      }
    },
    /**
     * If today is within current view boundaries, set to today.
     * Else, set to the first day of the week
     */
    async setAsDay() {
      if (!this.week) return;
      let start, end;
      if (this.viewStartMS < Date.now() && this.viewEndMS > Date.now()) start = startOfDay(Date.now()).valueOf();
      else start = this.viewStartMS;
      end = start + ONE_DAY;
      /* console.log("@setAsDay", start, Date.now(), end); */
      ViewController.switchView(this.userID, start, end);
      this.$store.dispatch("calendar/setWeek", false);
    },
    scrollToCurrentHour() {
      const rowHour = format(Math.floor(Date.now() / FIFTEEN_MINUTES) * FIFTEEN_MINUTES, "HH:mm");
      /* console.log("rowHour2: ", rowHour); */
      let rowHourElement = document.querySelector(`[data-rowhour="${rowHour}"]`);
      /* console.log("rowHourElement: ", rowHourElement); */
      // rowHourElement.closest("tbody").scrollTop =
      //   rowHourElement.offsetTop - 200;
      if (rowHourElement) {
        var elementPosition = rowHourElement.getBoundingClientRect().top;
        var offsetPosition = elementPosition - 475;
        rowHourElement.closest("tbody").scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });
        rowHourElement.closest("tbody").scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });
      }
    },
    async refreshCalendarData(manual) {
      // TODO: only request upcoming/matchpool
      /* console.log("@refreshCalendarData: ", manual); */
      await ViewController.refreshCurrentView(this.user._id, this.viewStartMS, this.viewEndMS);
      if (manual) {
        /* console.log("displaying message"); */
        this.displayAlert(0);
      }
    },
    displayAlert(messageNum) {
      if (messageNum == 0) {
        this.alertMessage = "Successfully refreshed calendar data!";
        this.alertStyle = "background-color: #e1fff8; color: #333; width: 350px;";
      }
      this.showAlert = true;
      let self = this;
      function resetAlert() {
        /* console.log("Alert false"); */
        self.showAlert = false;
        self.alertMessage = "";
        self.alertStyle = "";
      }
      setTimeout(resetAlert, 3000);
    },
    /* ======== TIMER METHODS ======== */
    removeSelectionsInPast() {
      /* only removes first datetime */
      if (this.selectedDateTimes[0] < Date.now()) {
        this.$store.dispatch("selections/cancelSelection", this.selectedDateTimes[0]);
      }
    },
    async completePastMatch() {
      if (this.nextMatchDateTimeMS > 0 && this.nextMatchDateTimeMS <= Date.now() - SESSION_LENGTH_MS) {
        /* await this.computeUserSessionConclusions(); */
        this.$store.dispatch("upcomingMatches/deleteUpcomingMatch", this.nextMatchDateTimeMS);
      }
    },
    async changeViewIfNecessary() {
      if (startOfDay(Date.now()) != this.lastCheckDateTime) {
        /* if current time went over the calendar view end, switch view */
        if (
          this.lastCheckDateTime.valueOf() >= this.viewStartMS &&
          this.lastCheckDateTime.valueOf() <= this.viewEndMS &&
          this.viewEndMS < Date.now()
        ) {
          let start, end;
          if (this.week) {
            start = startOfISOWeek(Date.now());
            end = start + ONE_WEEK;
          } else {
            start = startOfDay(Date.now());
            end = start + ONE_DAY;
          }
          await ViewController.switchView(this.user._id, start, end);
        }
      }
      /* finally, reset check time */
      this.lastCheckDateTime = startOfDay(Date.now());
    },

    /** ========== SESSION SOUND NOTIFICATION ========== */
    async initSessionNotifier() {
      // Clear any existing interval first
      if (this.sessionNotifierIntervalId) {
        clearInterval(this.sessionNotifierIntervalId);
        this.sessionNotifierIntervalId = null;
      }
      const isEnabled = this.user.notificationPreferences?.sessionStartSoundNotification?.enabled;
      const sessionStartTimeMS = this.nextSessionDateTimeMS; // null if no session
      console.log("@initSessionNotifier: ", isEnabled, sessionStartTimeMS);
      if (isEnabled && sessionStartTimeMS) {
        console.log("@initSessionNotifier: ", isEnabled, sessionStartTimeMS);
        const reminderTimeBeforeSessionMS = this.user.notificationPreferences.sessionStartSoundNotification.reminderTimeBeforeSessionMS;
        const reminderTime = new Date(sessionStartTimeMS - reminderTimeBeforeSessionMS);
        console.log("initSessionNotifier");
        const checkInterval = 1000; // Check every second
        const intervalId = setInterval(async () => {
          const now = new Date();
          if (now >= reminderTime) {
            console.log("here we go");
            clearInterval(intervalId);
            /** only play if tab is not visible/active */
            if (document[this.visibilityState] != "visible" && !document.hasFocus()) {
              const sound = await CalendarUtilities.getSessionNotificationSound(
                this.user.notificationPreferences.sessionStartSoundNotification.sound
              );
              const volume = this.user.notificationPreferences.sessionStartSoundNotification.soundVolume;
              ELYXUtils.playSound(sound, volume);
            }
          } else {
            console.log("not yet");
          }
        }, checkInterval);

        // Store the interval ID for cleanup
        this.sessionNotifierIntervalId = intervalId;
      } else if (isEnabled && !sessionStartTimeMS) {
        console.log("session notifier enabled but no session");
      }
    },
    /* ====== CALENDAR NAVIGATION ====== */
    /**
     * When user navigates between weeks
     * and arrives at the current week
     * which is the original week
     * scroll their calendar to the current hour.
     *
     * What if the view is day?
     */
    // scrollToCurrentHourIfNecessary() {
    //   let originalStart = this.originalWeekStart;
    //   let selectedWeek = this.currentWeekStart;
    //   if (isSameWeek(selectedWeek, originalStart)) {
    //     this.scrollToCurrentHour();
    //   }
    // },
  },
  watch: {
    $windowWidth: {
      handler() {
        /* console.log("@$windowWidth", this.$windowWidth); */
        /* if (this.$windowWidth > 1000) {
          if (this.user.subscription.status === SubscriptionStatuses.ACTIVE) {
            this.reinitAds();
          }
        } */
        if (this.$windowWidth <= 800) {
          this.setAsDay();
        }
        // else if (this.$windowWidth >= 801) {
        //   this.$store.commit("calendar/SET_WEEK", true);
        // }
      },
    },
    async onboardingComplete() {
      await BadgeMethods.getUserUnawardedBadges();
    },

    /** if next session changes, update session notifier */
    nextSessionDateTimeMS(newVal, oldVal) {
      console.log("@nextSessionDateTimeMS: ", newVal, oldVal);
      this.initSessionNotifier();
    },

    userNotificationPreferences: {
      deep: true,
      handler(newVal, oldVal) {
        if (newVal.sessionStartSoundNotification.enabled !== oldVal.sessionStartSoundNotification.enabled) {
          console.log("@userNotificationPreferences.sessionStartSoundNotification.enabled changed: ", newVal.sessionStartSoundNotification.enabled);
          this.initSessionNotifier();
        }
      },
    },
  },
};
</script>

<style scoped>
/* .page-container {
  height: 94vh;
} */
.booking-container {
  height: 100%;
}
.booking-container--mobile {
  height: calc(100% - 17px);
}
.calendar-view {
  height: 100%;
}
.calendar-container {
  overflow: hidden;
  /* background-color: #fbfbfb;
  background-color: #fdfdfd;
  background-color: #fbfbfb; */
  /* background-color: #fafafb; */
  display: flex;
  flex-direction: column;
  position: relative;
  /* background: #8e8ce03d; */
}
/* ==== UTILITIES ==== */
.full {
  width: 100%;
  height: 100%;
}
.flex-column-between {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}
/* .calendar-container {
  background: white;
} */
.unicorn-container {
  /* width: 90%; */
  /* max-width: 400px; */
  width: 300px;
  height: 250px;
  /* height: 100%; */
  min-height: 250px;
  box-sizing: border-box;
  margin-top: 27px;
  margin-bottom: 27px;
  margin-left: auto;
  margin-right: auto;
  background-color: #fff;
  box-shadow: 0 1px 4px 0 hsl(0deg 0% 70% / 42%);
  /* border: 1px solid transparent; */
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}
.supportUs {
  width: 90%;
  margin: auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-top: -15px;
}
.support-text {
  font-size: 12px;
  margin: 0 auto;
  text-align: center;
  margin-top: 5px;
  margin-bottom: 8px;
}
.buycoffee {
  border-radius: 360px;
  background-color: yellow;
  padding: 12px 0px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 700;
  border: 1px solid #505050;
  width: 92%;
  margin: 0px auto;
}
.buycoffee:hover {
  background-color: #f3f301;
}
.transparentBlock {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  position: absolute;
  left: 0px;
  top: 0px;
  width: 100%;
  height: 100%;
}
.unicorn-line-1 {
  font-size: 22px;
  font-weight: 600;
  max-width: 90%;
}
.unicorn-line-2 {
  margin-top: 10px;
  max-width: 82%;
  line-height: 18px;
}
.unicorn-line-3 {
  margin-top: 50px;
  max-width: 90%;
  font-weight: 600;
  font-size: 18px;
}
.mr-unicorn {
  display: inline-block;
  margin-right: 3px;
}
.unicorn-block {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  min-height: 250px;
  border-radius: 6px;
  overflow: hidden;
  z-index: 9999;
}

#container-d05ecff270b089951afb436e30dff25b {
  background-color: white;
}

.buycoffee:hover .mr-unicorn {
  animation-name: wave-animation; /* Refers to the name of your @keyframes element below */
  animation-duration: 2.5s; /* Change to speed up or slow down */
  animation-iteration-count: infinite; /* Never stop waving :) */
  transform-origin: 70% 70%; /* Pivot around the bottom-left palm */
}

/* TEMPORARY NO ADS STYLES */
/* .supportUs {
  margin-top: 40px;
} */
.sidebar-tasks {
  padding: 0px;
}
</style>
